import React, { useState } from 'react';
import { Camera, Sliders, Eye, Sparkles, CheckCircle2 } from 'lucide-react';
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from './ui/tooltip';
import { PersonalizationWidgetCompactV9 } from './PersonalizationWidgetCompactV9';

interface FreemiumJourneyBarV8Props {
  currentStep: 1 | 2 | 3 | 4;
  isPersonalized: boolean;
  onStepClick?: (stepId: number) => void;
  personalizationData?: {
    strategy?: number;
    capabilities?: number;
    readiness?: number;
    docsCount?: number;
  };
  onPersonalizationClick?: () => void;
}

const steps = [
  { 
    id: 1, 
    label: 'Snapshot',
    sublabel: 'Industry Insights',
    icon: Camera,
    description: 'View top data priorities shaping healthcare today.',
  },
  { 
    id: 2, 
    label: 'Personalize',
    sublabel: 'Your Profile',
    icon: Sliders,
    description: 'Enter a few details to see tailored results.',
  },
  { 
    id: 3, 
    label: 'Data Strategy Overview',
    sublabel: 'Your Summary',
    icon: Eye,
    description: 'View your organization\'s personalized data priorities and strategic recommendations.',
  },
  { 
    id: 4, 
    label: 'Upgrade to DVA Pro',
    sublabel: 'Advanced Insights',
    icon: Sparkles,
    description: 'Unlock advanced ROI modeling, roadmaps, and governance tools with DVA Pro.',
  },
];

// NOTE: Parent components should navigate to /personalize for step 2
// Example in parent: if (stepId === 2) navigate("/personalize")

export function FreemiumJourneyBarV8({ 
  currentStep, 
  isPersonalized, 
  onStepClick,
  personalizationData,
  onPersonalizationClick
}: FreemiumJourneyBarV8Props) {
  const [hoveredStep, setHoveredStep] = useState<number | null>(null);

  return (
    <div className="flex items-stretch gap-4 justify-end">
      {/* Main Journey Bar - expands to fill space */}
      <div className="flex-1 bg-white rounded-lg shadow-md border border-[#e5e7eb]">
        <div className="px-4 pt-2 pb-3">
          {/* Journey Steps */}
          <div className="flex items-center justify-between">
            <TooltipProvider delayDuration={100}>
              {steps.map((step, index) => {
                const StepIcon = step.icon;
                const isUpgradeStep = step.id === 4;
                
                return (
                  <React.Fragment key={step.id}>
                    <Tooltip>
                      <TooltipTrigger asChild>
                        <div 
                          className="flex flex-col items-center flex-1 group cursor-pointer"
                          onMouseEnter={() => setHoveredStep(step.id)}
                          onMouseLeave={() => setHoveredStep(null)}
                          onClick={() => onStepClick?.(step.id)}
                        >
                          <div className="relative">
                            {/* Step circle */}
                            <div
                              className={`w-9 h-9 rounded-full flex items-center justify-center transition-all duration-300 ${
                                step.id < currentStep
                                  ? 'bg-[#18365E] text-white'
                                  : step.id === currentStep
                                  ? isUpgradeStep
                                    ? 'bg-[#FF6E4C] text-white shadow-lg'
                                    : 'bg-[#FF6E4C] text-white shadow-lg'
                                  : isUpgradeStep
                                  ? 'bg-white text-[#FF6E4C] border-2 border-[#FF6E4C]/40'
                                  : 'bg-white text-[#18365E] border-2 border-[#18365E]/40'
                              } ${hoveredStep === step.id ? 'scale-110 shadow-xl' : ''}`}
                            >
                              {step.id < currentStep ? (
                                <CheckCircle2 className="w-4 h-4" />
                              ) : (
                                <StepIcon className="w-4 h-4" />
                              )}
                            </div>
                            
                            {/* Active step glow animation */}
                            {step.id === currentStep && (
                              <div className="absolute inset-0 rounded-full bg-[#FF6E4C]/30 animate-ping" />
                            )}
                          </div>
                          
                          {/* Step labels */}
                          <span
                            className={`mt-1.5 text-xs text-center max-w-[110px] transition-all ${
                              step.id === currentStep 
                                ? isUpgradeStep ? 'text-[#FF6E4C]' : 'text-[#0A2540]'
                                : isUpgradeStep ? 'text-[#FF6E4C]' : 'text-[#18365E]'
                            }`}
                            style={{ fontWeight: step.id === currentStep ? 700 : 600 }}
                          >
                            {step.label}
                          </span>
                          
                          {/* Sublabel */}
                          <span
                            className={`text-[10px] text-center transition-all ${
                              isUpgradeStep ? 'text-[#FF6E4C]/70' : 'text-[#717182]'
                            }`}
                            style={{ fontWeight: 500 }}
                          >
                            {step.sublabel}
                          </span>
                          
                          {/* Active step underline glow */}
                          {step.id === currentStep && (
                            <div className="mt-1 h-0.5 w-16 bg-gradient-to-r from-transparent via-[#FF6E4C] to-transparent animate-pulse" />
                          )}
                        </div>
                      </TooltipTrigger>
                      <TooltipContent 
                        side="bottom" 
                        className={`${isUpgradeStep ? 'bg-[#FF6E4C]' : 'bg-[#0A2540]'} text-white border-0 max-w-xs`}
                      >
                        <p className="text-xs" style={{ fontWeight: 500 }}>{step.description}</p>
                      </TooltipContent>
                    </Tooltip>
                    
                    {/* Connector line between steps */}
                    {index < steps.length - 1 && (
                      <div
                        className={`h-0.5 flex-1 mx-1.5 mt-[-42px] transition-all duration-500 ${
                          step.id < currentStep 
                            ? index === 2 ? 'bg-[#18365E]' : 'bg-[#18365E]'
                            : 'bg-[#e5e7eb]'
                        }`}
                      />
                    )}
                  </React.Fragment>
                );
              })}
            </TooltipProvider>
          </div>
          
          {/* Legend - Inline and Compact */}
          <div className="flex items-center justify-center gap-3 mt-3 pt-2 border-t border-[#e5e7eb]">
            <div className="flex items-center gap-1.5">
              <div className="w-2 h-2 rounded-full bg-[#18365E]" />
              <span className="text-[10px] text-[#717182]" style={{ fontWeight: 600 }}>
                Freemium Experience (Steps 1–3)
              </span>
            </div>
            
            <div className="w-px h-3 bg-[#e5e7eb]" />
            
            <div className="flex items-center gap-1.5">
              <div className="w-2 h-2 rounded-full bg-[#FF6E4C]" />
              <span className="text-[10px] text-[#717182]" style={{ fontWeight: 600 }}>
                Upgrade for Full Access (Step 4)
              </span>
            </div>
          </div>
        </div>
      </div>

      {/* Personalization Widget - fixed width, right-aligned, height-matched */}
      <div className="hidden lg:block shrink-0 w-[240px]">
        <PersonalizationWidgetCompactV9 
          personalizationData={personalizationData}
          onContinueClick={onPersonalizationClick}
        />
      </div>
    </div>
  );
}